import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useSearchParams } from 'next/navigation';
import classNames from 'classnames';
import { m } from 'framer-motion';
import { useIsMobile } from '@hooks';
import { SIZES } from '@styles/media';
import Header from '@components/DesignLibrary/Header';
import Search from '@components/DesignLibrary/Atoms/Search';
import Filters, { FiltersProps } from '@components/DesignLibrary/Filters';
import PaginationControls from '@components/DesignLibrary/PaginationControls';
import deserializeQueryStringItem from '@helpers/deserializeQueryStringItem';
import useHighlightSearchTerm from '@hooks/useHighlightSearchTerm';
import DatePicker from '@components/FormFields/DatePicker';
import { useQueryState } from 'nuqs';

import TableView, { TableColumn, TableRow } from './TableView';
import ListAndDetailsSubLayoutContext from './Context';
import ListView from './ListView';
import s from './ListAndDetailsSubLayout.module.scss';

type Props = {
  getDetailPagePath: (args: { id: string; creatorSlug?: string }) => string | null;
  header: Omit<Parameters<typeof Header>[0], 'children'>;
  ifNoItemsText?: string;
  isOneColumn: boolean;
  isFetchingList?: boolean;
  items: Array<Parameters<typeof ListView>[0]['items'][number] & { id: string }>;
  sections?: Parameters<typeof ListView>[0]['sections'];
  itemsCount?: number;
  onSearch?: (newSearch: string) => void | Promise<void>;
  selectedId?: string | null;
  pagination?: {
    page: number;
    numPages: number;
    onChangePage: Parameters<typeof PaginationControls>[0]['onChangePage'];
  };
  dateFilter?: {
    startDate: string | null;
    endDate: string | null;
  };
  setDateFilter?: (values: { startDate: string | null; endDate: string | null }) => void;
  filters?: FiltersProps;
  reloadListView?: () => Promise<void>;
  isTableViewOnly?: boolean;
  hideTabs?: boolean;
  hideTableToggle?: boolean;
  resourceType: string;
  tableView?: {
    columns: TableColumn[];
    rows: TableRow[];
  };
  secondaryTabs?: Parameters<typeof Header>[0]['tabs'];
  openTableView?: boolean;
  searchKey?: number;
};

const formatDate = (date?: string | null): string => {
  const tempDate = date ? new Date(date) : new Date();
  return tempDate.getFullYear().toString().concat('-', (tempDate.getMonth() + 1).toString(), '-', tempDate.getDate().toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false }));
};

const ListAndDetailsSubLayout: React.FC<Props> = ({
  children,
  getDetailPagePath,
  header,
  ifNoItemsText,
  isOneColumn,
  isFetchingList,
  items,
  sections,
  itemsCount,
  onSearch,
  pagination,
  selectedId,
  dateFilter,
  setDateFilter,
  filters,
  reloadListView,
  resourceType,
  isTableViewOnly = false,
  hideTabs = false,
  hideTableToggle = false,
  tableView,
  openTableView = false,
  secondaryTabs,
  searchKey,
}) => {
  const urlQueryObject = useSearchParams();
  const [isTableView, setIsTableView] = useState(isTableViewOnly || openTableView);
  const [routingTo, setRoutingTo] = useState('');
  const [searchQuery, setSearchQuery] = useQueryState('search');
  const [startDateQuery, setStartDateQuery] = useQueryState('startDate');
  const [endDateQuery, setEndDateQuery] = useQueryState('endDate');
  const [searchTerm, setSearchTerm] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const getDetailPagePathRef = useRef(getDetailPagePath);
  getDetailPagePathRef.current = getDetailPagePath;
  const reloadListViewRef = useRef(reloadListView);
  reloadListViewRef.current = reloadListView;
  const isMobile = useIsMobile(SIZES.xl);
  const router = useRouter();
  const creatorSlug = deserializeQueryStringItem(router.query.creatorSlug);

  const isDetailView = Boolean(selectedId);
  const showTable = isTableView;
  const showList = !showTable && (!isOneColumn || !isDetailView);
  const showDetails = !showTable && (!isOneColumn || isDetailView);

  const firstItemId = items && items.length > 0 ? items[0].id : undefined;

  const reloadListViewCallback = useCallback(async () => {
    if (reloadListViewRef.current) {
      reloadListViewRef.current();
    }
  }, []);

  useEffect(() => {
    setIsTableView(isTableViewOnly || openTableView);
  }, [resourceType, openTableView, isTableViewOnly]);

  useEffect(() => {
    if (searchQuery && onSearch) {
      setSearchTerm(searchQuery);
      onSearch(searchQuery);
    }
  }, [searchQuery, onSearch]);

  useEffect(() => {
    if (searchKey) {
      setSearchTerm('');
    }
  }, [searchKey]);

  useEffect(() => {
    if ((startDateQuery || endDateQuery) && setDateFilter) {
      setDateFilter({ endDate: endDateQuery, startDate: startDateQuery });
    }
  }, [startDateQuery, endDateQuery, setDateFilter]);

  useEffect(() => {
    // If this is not the detail view, and we want to show both columns, and we've loaded the list.
    // Then we want to router.push to the detail page.
    if (!isDetailView && !isOneColumn && firstItemId && !isTableView && !isTableViewOnly) {
      const path = getDetailPagePathRef.current({ creatorSlug: creatorSlug ?? undefined, id: firstItemId });
      if (path) {
        const queryString = `${urlQueryObject}`;
        router.push(`${path}?${queryString}`);
      }
    }
  }, [creatorSlug, firstItemId, isOneColumn, isTableView, isDetailView, isTableViewOnly, router, urlQueryObject]);

  useEffect(() => {
    router.events.on('routeChangeStart', url => {
      setRoutingTo(url);
    });
    router.events.on('routeChangeComplete', () => {
      setRoutingTo('');
    });
  }, [router, setRoutingTo]);

  useHighlightSearchTerm(searchTerm, [listRef.current, contentRef.current].filter((el): el is HTMLDivElement => el !== null));

  const contextValue = useMemo(
    () => ({
      reloadListView: reloadListViewCallback,
    }),
    [reloadListViewCallback],
  );

  const handleToggleTableView = (isSet: boolean): void => {
    setIsTableView(isSet);
  };

  const handleCloseTableView = (): void => {
    setIsTableView(false);
  };

  return (
    <ListAndDetailsSubLayoutContext.Provider value={contextValue}>
      <div
        className={s.wrapper}
        {...(creatorSlug && {
          style: {
            width: '100%',
          },
        })}
      >
        {(showList || showTable) && (
          <div
            className={classNames(s.listContent, showDetails || showTable ? s.withDetails : s.noDetails, (showTable || isTableViewOnly) && s.table)}
          >
            <Header
              {...header}
              tabs={hideTabs ? undefined : header.tabs}
              toggle={
                tableView && !isTableViewOnly && !hideTableToggle
                  ? {
                    isChecked: isTableView,
                    label: 'Table view',
                    onChange: handleToggleTableView,
                  }
                  : undefined
              }
            />
            {secondaryTabs && !hideTabs && <div className={s.secondaryTabs}>
              {secondaryTabs.map(sTab => {
                const STabIcon = sTab.icon;
                return (
                  <button key={sTab.label} onClick={sTab.onClick} disabled={sTab.selected}>
                    {sTab.selected && <div className={s.secondaryTabsSelectedTint} />}
                    {STabIcon && <STabIcon size={16} weight={sTab.selected ? 'fill' : 'regular'} />}
                    {sTab.label}
                  </button>
                );
              })}</div>}
            {isTableView && dateFilter && setDateFilter && <>
              <div className={s.dateFilter}>
                <DatePicker
                  onChange={(value: string) => {
                    setStartDateQuery("");
                    setDateFilter({ endDate: dateFilter.endDate, startDate: value });
                  }}
                  value={dateFilter.startDate || startDateQuery}
                  max={formatDate(dateFilter.endDate)}
                  label="Created start date"
                  inlineLabel
                />
                <DatePicker
                  onChange={(value: string) => {
                    setEndDateQuery("");
                    setDateFilter({ endDate: value, startDate: dateFilter.startDate });
                  }}
                  value={dateFilter.endDate || endDateQuery}
                  min={formatDate(dateFilter.startDate) || undefined}
                  max={formatDate()}
                  label="Created end date"
                  inlineLabel
                  endOfDay
                />
              </div>
            </>}
            {filters && <Filters {...filters} />}
            {onSearch && (
              <Search
                key={searchKey || 0}
                onChange={query => {
                  setSearchTerm(query);
                  onSearch(query);
                  setSearchQuery('');
                }}
                defaultValue={searchTerm}
              />
            )}
            {itemsCount ? <div className={s.itemsCount}>{Intl.NumberFormat().format(itemsCount)} total</div> : null}
            <div ref={listRef} key={`${router.pathname}-list${tableView ? '-table' : ''}`} style={{ display: 'contents' }}>
              {showList && !isTableViewOnly && (
                <ListView
                  ifNoItemsText={ifNoItemsText}
                  isFetching={isFetchingList}
                  items={items}
                  selectedId={selectedId}
                  routingTo={routingTo}
                  sections={sections}
                />
              )}
              {(showTable || isTableViewOnly) && tableView && (
                <TableView columns={tableView.columns} rows={tableView.rows} isFetching={isFetchingList} onCloseTable={handleCloseTableView} />
              )}
            </div>
            {pagination && (
              <PaginationControls
                className={s.paginationControls}
                page={pagination.page}
                numPages={pagination.numPages}
                onChangePage={pagination.onChangePage}
              />
            )}
          </div>
        )}

        {showDetails && !isTableViewOnly && (
          <m.div
            {...(!isMobile && {
              animate: { opacity: 1 },
              initial: { opacity: 0 },
              transition: { duration: 0.5, ease: [0.25, 1, 0.5, 1] },
            })}
            className={s.mainContent}
            ref={contentRef}
            key={`${router.pathname}-content-${searchTerm.length < 3 ? '' : searchTerm}`}
          >
            {children}
          </m.div>
        )}
      </div>
    </ListAndDetailsSubLayoutContext.Provider>
  );
};

export default ListAndDetailsSubLayout;
